import React, { ComponentProps, ComponentType } from 'react';
import HBFieldControl from './HBFieldControl';
import {
    HBFieldControlProps,
    HBFieldControlRenderProps
} from './HBFieldControl.types';

/**
 * Compile-time map of every HBFieldControl prop except `render`. Typed as a
 * `Record` of the prop union so adding or removing an HBFieldControl prop forces
 * this map to be updated — keeping the field/control prop split in sync.
 */
const FIELD_PROP_KEY_MAP: Record<
    keyof Omit<HBFieldControlProps, 'render'>,
    true
> = {
    id: true,
    label: true,
    ariaLabel: true,
    subLabel: true,
    caption: true,
    placement: true,
    reserveLabelSpace: true,
    reserveMessageSpace: true,
    messageLayout: true,
    margin: true,
    size: true,
    labelSize: true,
    labelAlignment: true,
    className: true,
    errorMessage: true,
    hintMessage: true,
    showHintMessage: true,
    disabledMessage: true,
    tooltip: true,
    tooltipAriaLabel: true,
    tooltipIcon: true,
    tooltipIconColor: true,
    tooltipIconHoverColor: true,
    tooltipKeepOpenOnHover: true,
    tooltipEnableInitialOpen: true,
    tooltipShouldSupportClickOutside: true,
    onTooltipOpen: true,
    width: true,
    name: true,
    value: true,
    onChange: true,
    onBlur: true,
    required: true,
    disabled: true,
    error: true,
    maxLength: true
};

const FIELD_PROP_KEYS = new Set<string>(Object.keys(FIELD_PROP_KEY_MAP));

/** The control's own props, minus the ones HBFieldControl wires through the render slot. */
type ControlOwnProps<C extends ComponentType<any>> = Omit<
    ComponentProps<C>,
    keyof HBFieldControlRenderProps
>;

/** Public props of a field built with `createHBField`: field chrome + the control's own props. */
export type HBFieldOf<C extends ComponentType<any>> = Omit<
    HBFieldControlProps,
    'render'
> &
    ControlOwnProps<C>;

interface CreateHBFieldOptions<C extends ComponentType<any>> {
    /** Props applied to every rendered control (e.g. `{ isNewHBMode: true }`). Overridable per instance. */
    defaultControlProps?: Partial<ComponentProps<C>>;
    /** React DevTools display name for the generated field. */
    displayName?: string;
}

/**
 * Builds a typed field wrapper around any control by pairing it with
 * HBFieldControl. The returned component takes the field chrome props plus the
 * control's own props, splits them, and spreads the wired bundle onto the control:
 *
 * ```tsx
 * const HBInputField = createHBField(HBInput, { defaultControlProps: { isNewHBMode: true } });
 * <HBInputField id="email" label="Email" placeholder="you@example.com" testId="email" />
 * ```
 *
 * Chrome props (label, hint, error…) and the wired props (value, onChange, id,
 * aria…) route to HBFieldControl; everything else (placeholder, size, testId…)
 * passes straight to the control.
 */
export function createHBField<C extends ComponentType<any>>(
    Control: C,
    options?: CreateHBFieldOptions<C>
) {
    const RenderControl = Control as ComponentType<any>;

    const HBField = (props: HBFieldOf<C>) => {
        const fieldProps: Record<string, unknown> = {};
        const controlProps: Record<string, unknown> = {};

        (Object.keys(props) as string[]).forEach(key => {
            if (FIELD_PROP_KEYS.has(key)) {
                fieldProps[key] = (props as Record<string, unknown>)[key];
            } else {
                controlProps[key] = (props as Record<string, unknown>)[key];
            }
        });

        return (
            <HBFieldControl
                {...(fieldProps as Omit<HBFieldControlProps, 'render'>)}
                render={control => (
                    <RenderControl
                        {...control}
                        {...options?.defaultControlProps}
                        {...controlProps}
                    />
                )}
            />
        );
    };

    HBField.displayName =
        options?.displayName ??
        `HBField(${Control.displayName || Control.name || 'Control'})`;

    return HBField;
}
